import React from "react";
import { pressRelease } from "../../constant/pressRelease";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import Eyebrow from "../common/Eyebrow";

const PressRelease = () => {
  return (
    <>
      <Helmet>
        <title>
          QuantaRock Press Releases | Data Center News, Colocation & Cloud
          Updates
        </title>

        <meta
          name="description"
          content="Explore the latest QuantaRock press releases, media coverage, and enterprise infrastructure announcements including data centers, colocation services, cloud solutions, and IT innovation updates across India."
        />

        <meta
          name="keywords"
          content="QuantaRock press release, data center news India, colocation services press release, cloud infrastructure updates, enterprise IT news, edge data center launch, server colocation India, cloud hosting announcements, data center company India"
        />

        <meta
          property="og:title"
          content="QuantaRock Press Releases | Enterprise Infrastructure News"
        />

        <meta
          property="og:description"
          content="Stay updated with QuantaRock's latest announcements on data centers, cloud services, colocation solutions, edge infrastructure, and enterprise technology innovations."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://quantarock.in/resources/press-release"
        />

        <meta
          property="og:image"
          content="https://quantarock.in/og-image.jpg"
        />

        <meta name="robots" content="index, follow" />

        <link
          rel="canonical"
          href="https://quantarock.in/resources/press-release"
        />
      </Helmet>

      <section className="min-h-screen bg-[#f8fafc] py-20 px-6 md:px-14">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="max-w-7xl mx-auto mb-20"
        >
          <div className="bg-white rounded-[32px] shadow-xl border border-gray-100 overflow-hidden">
            <div className="grid lg:grid-cols-2 items-center">
              {/* Left Content */}
              <div className="p-8 md:p-14">
                <span className="inline-block px-4 py-2 rounded-full bg-[var(--website-secondary-color)] text-[var(--on-secondary)] font-semibold text-sm mb-5">
                  Media Coverage & Industry Announcements
                </span>

                <h1 className="text-[var(--website-primary-color)] text-3xl md:text-4xl font-bold leading-tight mb-6">
                  QuantaRock Press Releases,
                  <br />
                  Data Center News & Media Updates
                </h1>

                <p className="text-gray-600 text-base leading-relaxed mb-6">
                  Stay informed with the latest QuantaRock press releases
                  covering data center expansion, colocation services, cloud
                  infrastructure, edge computing, dedicated servers, and
                  enterprise IT innovation across India.
                </p>

                <p className="text-gray-600 text-base leading-relaxed mb-8">
                  From new edge data center launches in major metro cities to
                  scalable colocation solutions for businesses, our media
                  announcements highlight how QuantaRock is transforming digital
                  infrastructure for enterprises, startups, and growing
                  organizations.
                </p>

                <div className="flex flex-wrap gap-4">
                  <div className="bg-gray-50 px-5 py-4 rounded-2xl border">
                    <p className="text-2xl font-bold text-black">Pan India</p>
                    <p className="text-sm text-gray-500">
                      Data Center Presence
                    </p>
                  </div>

                  <div className="bg-gray-50 px-5 py-4 rounded-2xl border">
                    <p className="text-2xl font-bold text-black">Enterprise</p>
                    <p className="text-sm text-gray-500">
                      IT Infrastructure Solutions
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Visual */}
              <div className="relative h-full min-h-[450px] overflow-hidden">
                {/* Background Image */}
                <img
                  src={require("../../assets/press-release-banner.webp")}
                  alt="QuantaRock Data Center Press Release"
                  className="absolute inset-0 w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* SEO Intro Section */}
        <div className="max-w-7xl mx-auto mb-14 text-center">
          <Eyebrow>Newsroom</Eyebrow>
          <h2 className="mt-3 text-3xl font-bold text-[var(--website-primary-color)] mb-4">
            Latest Data Center, Cloud & Colocation Industry News
          </h2>

          <p className="text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Explore verified press releases and official media announcements
            from QuantaRock Systems — a trusted provider of colocation services,
            managed hosting, enterprise cloud solutions, edge data centers, and
            IT infrastructure consulting. Discover how businesses across India
            are scaling faster with secure, high-performance digital
            infrastructure solutions.
          </p>
        </div>

        {/* Press Release Cards */}
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 xl:grid-cols-3 gap-8">
          {pressRelease.map((release, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.5,
                delay: index * 0.08,
              }}
              whileHover={{ y: -10 }}
              className="group bg-white rounded-[28px] border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden"
            >
              {/* Badge */}
              <div className="p-6 pb-0">
                <div className="inline-block bg-slate-100 px-4 py-2 rounded-full text-xs font-semibold text-slate-600">
                  Official Press Release
                </div>
              </div>

              {/* Content */}
              <div className="p-7 pt-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-400">{release.date}</p>

                  <p className="text-sm font-medium text-gray-500">
                    {release.source}
                  </p>
                </div>

                <h3 className="text-xl font-bold text-[var(--website-primary-color)] leading-snug mb-4 group-hover:text-[var(--website-secondary-color)] transition duration-300">
                  {release.title}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed line-clamp-4">
                  {release.summary}
                </p>

                <div className="pt-6">
                  <a
                    href={release.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full py-3 rounded-xl bg-[var(--website-primary-color)] text-white font-semibold hover:bg-[var(--website-primary-color)] transition duration-300"
                  >
                    Read Full Press Release →
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
};

export default PressRelease;
